import { EditOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Form, Input, Row, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { useDispatch } from 'react-redux';
import { useCollections, useCollectionsState } from 'src/features/collections/collections.hooks';
import { createCollection, fetchCollections } from 'src/features/collections/collections.state';
import tileStyles from 'src/styles/tile.module.css';

import { AppDispatch } from 'src/index';
import { useForm } from 'antd/es/form/Form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';

const emptyEntity = { name: '' };

const CollectionsPage: React.FC = () => {
  const collections = useCollections();
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const collectionsState = useCollectionsState();

  const [name, setName] = useState<string | undefined>(undefined);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<{ name: string }>(emptyEntity);
  const [form] = useForm();

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createCollection(newEntity));

    if (createCollection.fulfilled.match(response)) {
      history.push(`/content/collections/${response.payload.id}`);
    }
  }, [dispatch, history, newEntity]);
  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchCollections({ params: { name: name } }));
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/collections'>
                <Space>
                  <ProfileTwoTone />
                  Коллекции
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            Коллекции
          </Typography.Title>
          <div>
            <Input
              autoFocus
              allowClear
              placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
              prefix={<SearchOutlined />}
              onChange={e => {
                setName(e.target.value);
              }}
              style={{ marginBottom: 10 }}
            />
            <LoaderWrapper loading={collectionsState.isPending}>
              <div className={styles.content}>
                {createMode && (
                  <Form
                    form={form}
                    validateTrigger='onBlur'
                    layout='vertical'
                    className={tileStyles.listTile}
                    onFinish={handleFormSubmit}
                  >
                    <Form.Item name='name' noStyle>
                      <Input
                        autoFocus
                        type='text'
                        name='name'
                        placeholder='Введите название коллекции'
                        onChange={ev => setNewEntity(e => ({ ...e, name: ev.target.value }))}
                      />
                    </Form.Item>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='submit'
                      type='primary'
                      disabled={newEntity.name === ''}
                    >
                      Создать
                    </Button>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='button'
                      type='default'
                      onClick={() => setCreateMode(false)}
                    >
                      Отменить
                    </Button>
                  </Form>
                )}
                {collections.length === 0 && (
                  <Row justify='center' style={{ width: '100%', margin: '16px 0' }}>
                    <Typography.Text>
                      <FormattedMessage id={'text.NothingFound'} />
                    </Typography.Text>
                  </Row>
                )}
                {collections.map(c => (
                  <div key={c.id} className={tileStyles.listTile}>
                    <Link to={`/content/collections/${c.id}/cards`} className={tileStyles.tileName}>
                      <Space>
                        <ProfileTwoTone />
                        {c.name}
                      </Space>
                    </Link>
                    <Space>
                      {c.disabled && <Tag>Заблокировано</Tag>}
                      <Button>
                        <EditOutlined />
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            </LoaderWrapper>
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={() => setCreateMode(true)}>
              Создать новую коллекцию
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CollectionsPageBlock: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCollections({}));
  }, [dispatch]);

  return <CollectionsPage />;
};

export default CollectionsPageBlock;
